import * as React from 'react'
import { FaIcon, faPrint, faGearRegular } from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { ReportType } from '@/app/constants/reportType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTimesheetPrintoutConfiguration } from '../hooks/useTimesheetPrintoutConfiguration'
import { useGeneratePrintout } from '../hooks/useGeneratePrintout'
import { usePrintJobGuard } from '../hooks/usePrintJobGuard'
import { getReportOptionLabelKey } from './PrintModal'
import { TimesheetPrintoutConfigurationModal } from './TimesheetPrintoutConfigurationModal'

interface Props {
  companyId: string
  driverIds: string[]
  dateFrom?: string
  dateTo?: string
}

/**
 * Szybkie wydruki ewidencji (WPF: grupa „Wydruki” + „Konfiguruj raporty”).
 * Zestaw raportów jest ustawiany per baza firmy i wspólny z WPF.
 */
export const TimesheetPrintShortcuts: React.FC<Props> = ({
  companyId,
  driverIds,
  dateFrom,
  dateTo,
}) => {
  const { t } = useLocalize()
  const { data: configuration } = useTimesheetPrintoutConfiguration(companyId)
  const { handleGeneratePrintout } = useGeneratePrintout()
  const { busyTooltip } = usePrintJobGuard()
  const [configOpen, setConfigOpen] = React.useState(false)
  // Pusta lista kierowców serwer dopełnia całą grupą - bez zaznaczenia nie drukujemy.
  const blockedText =
    busyTooltip ||
    (driverIds.length === 0 ? t('printout.no-subjects-selected') : undefined)

  const print = (reportType: ReportType) =>
    handleGeneratePrintout({
      companyId,
      driverIds,
      dateFrom: dateFrom ?? '',
      dateTo: dateTo ?? '',
      selectedReports: [reportType],
      generateToSeparateFiles: false,
      rememberSelected: false,
    })

  if (!configuration) {
    return null
  }

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-1">
        {configuration.reportTypes.map((reportType, index) => {
          const labelKey = getReportOptionLabelKey(reportType)
          const name = labelKey ? t(labelKey) : reportType
          return (
            <Tooltip
              key={reportType}
              portal
              text={name}
              onClick={() => print(reportType)}
              blockedText={blockedText ? `${name}: ${blockedText}` : undefined}
            >
              <div
                className="tw-relative tw-flex tw-h-5 tw-w-6 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]"
                aria-label={name}
              >
                <FaIcon icon={faPrint} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
                <span className="tw-absolute -tw-bottom-1 tw-right-0 tw-rounded-sm tw-bg-white tw-px-[1px] tw-text-[9px] tw-font-bold tw-leading-[10px] tw-text-[#2448bb]">
                  {index + 1}
                </span>
              </div>
            </Tooltip>
          )
        })}
        <Tooltip
          portal
          text={t('printout.configure-reports')}
          onClick={() => setConfigOpen(true)}
        >
          <div
            className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]"
            aria-label={t('printout.configure-reports')}
          >
            <FaIcon icon={faGearRegular} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
          </div>
        </Tooltip>
      </div>

      {configOpen && (
        <TimesheetPrintoutConfigurationModal
          companyId={companyId}
          configuration={configuration}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </>
  )
}
